import React from 'react';
import Button from '../ui/Button';
import Icon from '../Icon';
import AccountBar, { Crumb } from './AccountBar';
import type { AccountView } from '../../utils/accountRoutes';

interface AccountFormPageProps {
  /** Which account screen this is; the trail (Workspace / list / page) comes from it. */
  view: AccountView;
  /** The record's own name for the last crumb, when the page has one. */
  leaf?: string;
  /** Crumbs between the parent list and the page. */
  via?: readonly Crumb[];
  title: string;
  subtitle?: string;
  onSave: () => void;
  onCancel: () => void;
  saveLabel?: string;
  /** Disables Save (required fields missing, nothing to save). */
  canSave?: boolean;
  saving?: boolean;
  /** Hides Save entirely, for a record that can no longer be edited. */
  readOnly?: boolean;
  /** Extra buttons next to Save/Cancel. */
  extraActions?: React.ReactNode;
  children: React.ReactNode;
}

/**
 * A create/edit page for an account-level record: breadcrumb, Save and Cancel
 * in the bar at the top, the form below. Replaces the popup each of these used
 * to open, so the page has a URL of its own and survives a reload.
 */
export const AccountFormPage: React.FC<AccountFormPageProps> = ({
  view, leaf, via, title, subtitle, onSave, onCancel, saveLabel = 'Save', canSave = true,
  saving = false, readOnly = false, extraActions, children,
}) => (
  <div className="wt-container fade-in ap-page">
    <AccountBar
      view={view}
      leaf={leaf}
      via={via}
      actions={
        <>
          {extraActions}
          {/* Icon plus label; below ~560px the label hides (AccountShell.css) so the bar stays on one row. */}
          <Button variant="secondary" size="sm" icon={<Icon name={readOnly ? 'chevronLeft' : 'x'} size={14} />} onClick={onCancel} disabled={saving}>
            <span className="as-btn-label">{readOnly ? 'Back' : 'Cancel'}</span>
          </Button>
          {!readOnly && (
            <Button variant="primary" size="sm" icon={<Icon name="check" size={14} />} onClick={onSave} disabled={!canSave} loading={saving}>
              <span className="as-btn-label">{saving ? 'Saving…' : saveLabel}</span>
            </Button>
          )}
        </>
      }
    />
    <div className="ap-form">
      <h1 className="pl-title">{title}</h1>
      {subtitle && <p className="pl-subtitle">{subtitle}</p>}
      <div className="ap-form-body">{children}</div>
    </div>
  </div>
);

export default AccountFormPage;
